/**
 * Visual connector component that links level sections in the onboarding journey
 * Changes color from gray (locked) to level color (unlocked) to show progression
 */

import React from "react";
import { Box } from "@mantine/core";
import { IconArrowDown } from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";

interface JourneyConnectorProps {
  isUnlocked: boolean;
  color: string;
  onClick?: () => void;
}

/**
 * Animated connector showing progression between levels
 */
const JourneyConnector: React.FC<JourneyConnectorProps> = ({
  isUnlocked,
  color,
  onClick,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const height = isMobile ? 12 : 16;

  return (
    <Box
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        margin: "0 auto",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s ease",
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = "scale(1.1)";
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = "scale(1)";
        }
      }}
    >
      {/* Vertical line - Much smaller */}
      <Box
        style={{
          width: 3,
          height: `${height}px`,
          background: isUnlocked
            ? `linear-gradient(to bottom, ${color} 0%, ${color} 100%)`
            : "#e0e0e0",
          borderRadius: 2,
          opacity: isUnlocked ? 1 : 0.4,
          transition: "all 0.5s ease-in-out",
        }}
      />

      {/* Arrow indicator - Smaller */}
      <Box
        style={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: isUnlocked
            ? `linear-gradient(135deg, ${color} 0%, ${color} 100%)`
            : "#e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: isUnlocked ? 1 : 0.4,
          transition: "all 0.5s ease-in-out",
          boxShadow: isUnlocked
            ? `0 2px 6px ${color}33`
            : "0 1px 2px rgba(0,0,0,0.1)",
        }}
      >
        <IconArrowDown
          size={12}
          color="white"
          stroke={2.5}
          style={{
            filter: isUnlocked
              ? "drop-shadow(0 1px 2px rgba(0,0,0,0.3))"
              : "none",
          }}
        />
      </Box>

      {/* Bottom vertical line - Smaller */}
      <Box
        style={{
          width: 3,
          height: `${height}px`,
          background: isUnlocked
            ? `linear-gradient(to bottom, ${color} 0%, ${color} 100%)`
            : "#e0e0e0",
          borderRadius: 2,
          opacity: isUnlocked ? 1 : 0.4,
          transition: "all 0.5s ease-in-out",
        }}
      />
    </Box>
  );
};

export default JourneyConnector;
